import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useAuth } from "../hooks/useAuth.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { Pagination } from "../components/ui/pagination.js";
import { usePageBack } from "../context/PageBackContext.js";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PendingChange {
  id: string;
  employeeId: string;
  field: string;
  fieldLabel: string;
  currentValue: string | null;
  proposedValue: string | null;
  proposedAt: string;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
  proposedBy: { id: string; email: string; employee: { firstName: string; lastName: string } | null };
}

interface ListMeta { total: number; page: number; limit: number; totalPages: number }

// ─── Helpers ──────────────────────────────────────────────────────────────────

function decodeRole(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!));
    return (payload as Record<string, unknown>)["role"] as string ?? "";
  } catch { return ""; }
}

import { HR_ROLES } from "@hrms/types";

const inp: React.CSSProperties = {
  padding: "8px 10px", border: "1px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: "13px",
  background: "var(--color-surface-0)", outline: "none", width: "100%", boxSizing: "border-box",
};

// ─── Reject modal ─────────────────────────────────────────────────────────────

function RejectModal({ changeId, onClose, onDone }: { changeId: string; onClose: () => void; onDone: () => void }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    if (!notes.trim()) { setError("Please provide a reason."); throw new Error("validation"); }
    setBusy(true);
    try {
      await api.post(`/pending-changes/${changeId}/reject`, { reviewNotes: notes.trim() });
      onDone();
    } catch (err) {
      logError("PendingChangesPage.RejectModal.submit", err);
      setError("Rejection failed. Please try again.");
      throw err;
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}
    >
      <div
        style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", width: "min(420px, 95vw)", padding: "24px", display: "flex", flexDirection: "column", gap: "14px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: 0, fontSize: "15px", fontWeight: 700 }}>Reject Change Request</h3>
        <div>
          <label style={{ fontSize: "12px", fontWeight: 600, display: "block", marginBottom: "4px", color: "var(--color-surface-700)" }}>
            Reason *
          </label>
          <textarea
            value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder="Explain why this change is being rejected…"
            style={{ ...inp, resize: "vertical" }}
          />
        </div>
        {error && <p style={{ margin: 0, fontSize: "12px", color: "var(--color-error-fg)" }}>{error}</p>}
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={onClose} style={{ flex: 1, padding: "9px", border: "1px solid var(--color-surface-200)", background: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: "13px" }}>
            Cancel
          </button>
          <AsyncButton
            variant="destructive"
            className="flex-1"
            disabled={busy}
            onClick={() => submit()}
          >
            Reject
          </AsyncButton>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function PendingChangesPage() {
  const navigate = useNavigate();
  usePageBack("/admin", "Back to Admin");
  const { accessToken } = useAuth();
  const role = accessToken ? decodeRole(accessToken) : "";

  const [changes, setChanges]       = useState<PendingChange[]>([]);
  const [meta, setMeta]             = useState<ListMeta>({ total: 0, page: 1, limit: 25, totalPages: 1 });
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [page, setPage]             = useState(1);
  const [limit, setLimit]           = useState(25);
  const [approving, setApproving]   = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);

  const load = useCallback(async (p: number) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get<{ data: PendingChange[]; meta: ListMeta }>(
        `/admin/pending-changes?page=${p}&limit=${limit}`
      );
      setChanges(data.data);
      setMeta(data.meta);
    } catch (err) {
      logError("PendingChangesPage.load", err);
      setError("Failed to load pending changes.");
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { void load(page); }, [page, load]);

  async function approve(id: string) {
    setApproving(id);
    try {
      await api.post(`/pending-changes/${id}/approve`, {});
      void load(page);
    } catch (err) {
      logError("PendingChangesPage.approve", err);
      setError("Approval failed. Please try again.");
    } finally {
      setApproving(null);
    }
  }

  if (!HR_ROLES.has(role)) {
    return (
      <div style={{ padding: "var(--space-8)" }}>
        <p style={{ color: "var(--color-surface-700)" }}>You don't have access to this page.</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-surface-50)" }}>
      {/* Header */}
      <div style={{
        background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)",
        padding: "var(--space-5) var(--space-8)", display: "flex", alignItems: "center", gap: "12px",
      }}>
        <h1 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "var(--color-surface-900)" }}>Pending Change Requests</h1>
        {meta.total > 0 && (
          <span style={{
            background: "var(--color-warning-bg)", color: "var(--color-warning-fg)", border: "1px solid var(--color-warning-border)",
            padding: "2px 10px", borderRadius: "var(--radius-full)", fontSize: "12px", fontWeight: 700,
          }}>
            {meta.total} pending
          </span>
        )}
      </div>

      <div style={{ padding: "var(--space-6) var(--space-8)" }}>
        {error && (
          <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "var(--space-3)", borderRadius: "var(--radius-md)", marginBottom: "var(--space-4)", fontSize: "13px" }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "var(--space-12)", color: "var(--color-surface-600)" }}>Loading…</div>
        ) : changes.length === 0 ? (
          <div style={{
            textAlign: "center", padding: "var(--space-12)", color: "var(--color-surface-500)",
            background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)",
            border: "1px solid var(--color-surface-200)", fontSize: "14px",
          }}>
            No pending change requests. All clear!
          </div>
        ) : (
          <>
            <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-surface-200)", overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
                    {["Employee", "Field", "Current Value", "Proposed Value", "Requested By", "Submitted", ""].map((h) => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "11px", fontWeight: 700, color: "var(--color-surface-600)", textTransform: "uppercase", letterSpacing: "0.5px", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {changes.map((c, i) => (
                    <tr key={c.id} style={{ borderBottom: i < changes.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
                      <td style={{ padding: "12px 14px" }}>
                        <button
                          onClick={() => navigate(`/employees/${c.employee.id}/edit`)}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
                        >
                          <div style={{ fontWeight: 600, color: "var(--color-brand-600)", fontSize: "13px" }}>
                            {c.employee.firstName} {c.employee.lastName}
                          </div>
                          <div style={{ fontSize: "11px", color: "var(--color-surface-500)", fontFamily: "var(--font-mono)" }}>
                            {c.employee.employeeCode}
                          </div>
                        </button>
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: "13px", fontWeight: 600, color: "var(--color-surface-800)" }}>
                        {c.fieldLabel}
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: "12px", color: "var(--color-surface-600)", fontFamily: "var(--font-mono)", maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.currentValue != null ? String(c.currentValue).slice(0, 50) : <span style={{ color: "var(--color-surface-400)" }}>—</span>}
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: "12px", color: "var(--color-success-fg)", fontFamily: "var(--font-mono)", maxWidth: "160px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.proposedValue != null ? String(c.proposedValue).slice(0, 50) : <span style={{ color: "var(--color-surface-400)" }}>—</span>}
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: "12px", color: "var(--color-surface-700)" }}>
                        {c.proposedBy.employee
                          ? `${c.proposedBy.employee.firstName} ${c.proposedBy.employee.lastName}`
                          : c.proposedBy.email}
                      </td>
                      <td style={{ padding: "12px 14px", fontSize: "12px", color: "var(--color-surface-500)", whiteSpace: "nowrap" }}>
                        {new Date(c.proposedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button
                            disabled={!!approving}
                            onClick={() => void approve(c.id)}
                            style={{
                              padding: "5px 12px", background: "var(--color-success)", color: "#fff", border: "none",
                              borderRadius: "var(--radius-sm)", cursor: approving ? "default" : "pointer",
                              fontWeight: 600, fontSize: "12px", opacity: approving === c.id ? 0.6 : 1,
                            }}
                          >
                            {approving === c.id ? "…" : "Approve"}
                          </button>
                          <button
                            disabled={!!approving}
                            onClick={() => setRejectTarget(c.id)}
                            style={{
                              padding: "5px 12px", background: "var(--color-error-bg)", color: "var(--color-error-fg)",
                              border: "1px solid var(--color-error-border)", borderRadius: "var(--radius-sm)",
                              cursor: approving ? "default" : "pointer", fontWeight: 600, fontSize: "12px",
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination meta={meta} onPageChange={setPage} onPageSizeChange={(n) => { setLimit(n); setPage(1); }} />
          </>
        )}
      </div>

      {rejectTarget && (
        <RejectModal
          changeId={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onDone={() => { setRejectTarget(null); void load(page); }}
        />
      )}
    </div>
  );
}
